<script setup lang="ts">
import { vMaska } from 'maska/vue'
import type { MaskInputOptions, MaskOptions, MaskType } from 'maska'
import { type UIInputBaseProps, type UseFieldEmits, LiveValidateType } from '../../types'
import { ref, computed, useAttrs, watch, useFocus, useField, useId, onMounted, useResizeObserver, skipMaskLiterals } from '#imports'

// UIInput resolves function masks itself (see `maskaOptions`), so widen the
// function form to return maska's candidate-list shape.
type UIInputMask = Omit<MaskInputOptions, 'mask'> & {
    mask?: MaskType | ((input: string) => string[])
}

export interface UIInputProps extends UIInputBaseProps {
    /** Id of the input, also used by its label. Generated when omitted. */
    id?: string
    modelValue?: string | number
    type?: HTMLInputElement['type']
    mask?: UIInputMask
    hint?: string
}

const props = withDefaults(defineProps<UIInputProps>(), {
    modelValue: undefined,
    type: 'input',
    mask: undefined,
    suffix: undefined,
    testId: undefined,
    dataTest: undefined,
    errorMessages: undefined,
    rules: undefined,
    autofocus: false,
    autocomplete: undefined,
    hint: undefined,
    liveValidate: undefined,
    staticLabel: false,
})
const emit = defineEmits<UseFieldEmits>()

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()
const slots = defineSlots<{
    prepend?(props: {
        imageAttrs: {
            class: any[]
        }
        iconAttrs: {
            class: any[]
        }
        context: typeof context
    }): any
    default(props: typeof context): any
    hint?(props: Record<string, any>): any
    displayValue?(props: Record<string, any>): any
    append?(props: {
        imageAttrs: {
            class: any[]
        }
        iconAttrs: {
            class: any[]
        }
        context: typeof context
    }): any
    error?(): any
}>()

// Mirrors $input-field-inset in ui-input.scss.
const FIELD_INSET = 15

const prepend = ref<HTMLInputElement | null>(null)
const append = ref<HTMLInputElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)
const labelRef = ref<HTMLElement | null>(null)
const prependWidth = ref<number>(0)
const appendWidth = ref<number>(0)
const unmaskedValue = defineModel<string | number>('unmaskedValue')

const maskOptions = computed<MaskOptions>(() => {
    const currentMask = typeof props.mask?.mask === 'function'
        ? props.mask.mask(props.modelValue as string)
        : props.mask?.mask

    return {
        mask: currentMask,
        number: props.mask?.number,
        tokens: props.mask?.tokens,
        eager: true,
    }
})

const maskaOptions = computed<MaskInputOptions>(() => {
    return {
        ...maskOptions.value,
        onMaska: (detail: { masked: string, unmasked: string, completed: boolean }) => {
            const shouldValidate = props.liveValidate === LiveValidateType.INPUT || props.liveValidate === LiveValidateType.CHANGE || !!context.errorMessage.value

            context.handleChange(detail.masked, shouldValidate)
            unmaskedValue.value = detail.unmasked || detail.masked
        },
    }
})

// WebKit resolves the delete range before beforeinput, so cancel it and delete past literals by hand.
function onBeforeInput(event: InputEvent) {
    if (!props.mask?.mask || props.mask.number) return

    const direction = ({ deleteContentBackward: 'backward', deleteContentForward: 'forward' } as const)[event.inputType]
    if (!direction) return

    const input = event.target as HTMLInputElement
    const { selectionStart, selectionEnd, value } = input
    if (selectionStart === null || selectionStart !== selectionEnd) return

    const position = skipMaskLiterals(maskOptions.value, value, selectionStart, direction)
    if (position === selectionStart) return

    const start = direction === 'backward' ? position - 1 : position
    if (start < 0 || start >= value.length) return

    event.preventDefault()
    input.value = value.slice(0, start) + value.slice(start + 1)
    input.setSelectionRange(start, start)
    input.dispatchEvent(new InputEvent('input', { inputType: event.inputType, bubbles: true }))
}

const component = computed(() => {
    if (props.type === 'textarea') {
        return 'textarea'
    }

    return 'input'
})

const generatedId = useId()
const inputAndLabelId = computed(() => props.id || generatedId)
// Widen via computed: augmented DOM lib types don't treat HTMLInputElement as
// assignable to the HTMLElement useFocus wants (see UI/Popover.vue).
const focusTarget = computed<HTMLElement | null>(() => inputRef.value as HTMLElement | null)
const { focused } = useFocus(focusTarget)

const { context, on } = useField({
    props: () => props,
    emit,
    on: {
        enableChange: !props.mask,
        enableInput: !props.mask,
        forceValidateOnInput: props.liveValidate === LiveValidateType.INPUT,
        forceValidateOnBlur: props.liveValidate === LiveValidateType.BLUR,
        forceValidateOnChange: props.liveValidate === LiveValidateType.CHANGE,
    },
    options: {
        initialValue: props.modelValue
    }
})

// Slot contexts
const prependSlotContext = computed(() => {
    return {
        iconAttrs: {
            size: 24,
            class: [
                'input-field__icon',
                'input-field__icon--prepended'
            ]
        },
        imageAttrs: {
            width: 34,
            class: [
                'input-field__image',
                'input-field__image--prepended'
            ]
        },
        context,
        focus: focused.value
    }
})
const appendSlotContext = computed(() => {
    return {
        iconAttrs: {
            size: 24,
            class: [
                'input-field__icon',
                'input-field__icon--appended'
            ]
        },
        imageAttrs: {
            width: 34,
            class: [
                'input-field__image',
                'input-field__image--appended'
            ]
        },
        context,
        focus: focused.value
    }
})

function handlePrependWidth() {
    if (!slots?.prepend || !prepend.value) return

    prependWidth.value = prepend.value?.offsetWidth
}

function handleAppendWidth() {
    if (!slots?.append || !append.value) return

    appendWidth.value = append.value?.offsetWidth
}

// A mount-time measurement goes stale when the slot's content appears later:
// GameSearchOverlay renders its clear icon behind `v-if="hasQuery"`, so the
// append wrapper measures 0 until the field holds a value, and the padding it
// reserves has to grow with it.
useResizeObserver(prepend, handlePrependWidth)
useResizeObserver(append, handleAppendWidth)

watch(focused, (focus) => {
    emit('focus', focus)
}, {
    immediate: true
})

defineExpose({
    context,
    inputRef
})

onMounted(() => {
    handlePrependWidth()
    handleAppendWidth()

    if (props.autofocus) {
        setTimeout(() => {
            inputRef.value?.focus()
        }, 10)
    }
})
</script>

<template>
    <div
        :class="[
            'input-field',
            attrs.class,
            {
                'input-field--focused': (!!context.value.value || focused),
                'input-field--error': context.meta.validated && !context.meta.valid,
                'input-field--has-display-value': !!slots?.displayValue,
                'input-field--static': props.staticLabel,
            }
        ]"
        :style="attrs.style"
    >
        <div class="input-field__input-container">
            <div v-if="slots.prepend" ref="prepend" class="input-field__prepend">
                <slot name="prepend" v-bind="prependSlotContext" />
            </div>
            <!-- Input -->
            <component
                :is="component"
                :id="inputAndLabelId"
                ref="inputRef"
                v-maska="props.mask?.mask ? maskaOptions : undefined"
                :disabled="disabled"
                :name="name"
                :value="context.value.value"
                :type="props.type"
                v-bind="attrs"
                :placeholder="placeholder"
                :data-test="props.dataTest"
                class="input-field__input"
                :style="{
                    paddingRight: appendWidth ? `${FIELD_INSET + appendWidth + FIELD_INSET}px` : undefined,
                    paddingLeft: prependWidth ? `${FIELD_INSET + prependWidth + FIELD_INSET}px` : undefined,
                }"
                :autocomplete="props.autocomplete"
                v-on="on"
                @beforeinput="onBeforeInput"
            />

            <!-- Label -->
            <label
                ref="labelRef"
                class="input-field__label"
                :for="inputAndLabelId"
                :style="{
                    paddingRight: appendWidth ? `${FIELD_INSET + appendWidth}px` : undefined,
                    paddingLeft: prependWidth ? `${FIELD_INSET + prependWidth}px` : undefined,
                }"
            >
                <slot v-bind="{ ...context, focus: focused }" />
            </label>

            <div v-if="slots.displayValue && (focused || !!context.value.value)" class="input-field__display-value">
                <slot name="displayValue" v-bind="{ ...context, focus: focused }" />
            </div>

            <div v-if="slots.append" ref="append" class="input-field__append">
                <slot name="append" v-bind="appendSlotContext" />
            </div>
        </div>

        <!-- Hint -->
        <p v-if="(!!props.hint || !!slots.hint)" class="input-field__hint">
            <slot name="hint">
                {{ props.hint }}
            </slot>
        </p>

        <!-- Errors -->
        <div class="input-field__error">
            <slot name="error">
                <UIErrorMessages :messages="context.errors.value" :data-test="props.dataTest" />
            </slot>
        </div>
    </div>
</template>
